import { type MouseEventHandler, PureComponent, type ReactNode } from 'react';

import classNames from 'classnames';
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getMonth,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  setDate,
  setMonth,
  setYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import _isDate from 'lodash-es/isDate';
import _isEqual from 'lodash-es/isEqual';
import _isNumber from 'lodash-es/isNumber';
import _isPlainObject from 'lodash-es/isPlainObject';
import _isString from 'lodash-es/isString';

import InputNumber from '../../components/fields/input_number/input_number';
import VARIANTS from '../../constants/variants';
import { ICONS } from '../../icons/icon';
import { targetHtmlOrSvgElement } from '../../utils/event_target';
import { ui } from '../../utils/helpers';
import Button from '../button/button';
import type { FormFieldEvent } from '../forms/types';
import IconButton from '../icon_button/icon_button';
import {
  extractDateTimeInfo,
  getUserTimeFormat,
  validateHoursByTimeFormat,
} from '../input_date/utils';
import type { DateRange } from './default_options';
import TimePicker, { type TimeFormatOptions } from './time_picker/time_picker';

import mixins from '../../styles/core/mixins.module.css';
import styles from './calendar.module.css';

const stateMap: ('startDate' | 'endDate')[] = ['startDate', 'endDate'];

type Props = {
  isRangeMode: boolean;
  onChange: (
    value: Date | DateRange | undefined,
    event: FormFieldEvent,
  ) => void;
  value?: DateRange | Date | number | string;
  readOnly?: boolean;
  disabled?: boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
  initialMonth?: Date;
  withTimePicker?: boolean;
};

type State = {
  currentMonth: Date;
  startDate?: Date;
  endDate?: Date;
  yearMonthPicker?: boolean;
  dragging?: 0 | 1;
  selectedTimeFormat: TimeFormatOptions;
  hours?: string;
  minutes?: string;
  seconds?: string;
};

class Calendar extends PureComponent<Props, State> {
  static defaultProps = {
    isRangeMode: true,
    onChange: (): void => {},
  };

  state: State = this.getStateFromValue(this.props.value);

  componentDidMount(): void {
    document.addEventListener('mouseup', this.onMouseUp);
  }

  componentWillUnmount(): void {
    document.removeEventListener('mouseup', this.onMouseUp);
  }

  componentDidUpdate(prevProps: Props): void {
    if (prevProps.value !== this.props.value) {
      if (!_isEqual(prevProps.value, this.props.value)) {
        this.setState(this.getStateFromValue(this.props.value));
      }
    }
  }

  getStateFromValue(value: Date | string | DateRange | number | undefined): {
    currentMonth: Date;
    startDate?: Date;
    endDate?: Date;
    selectedTimeFormat: TimeFormatOptions;
    hours: string;
    minutes: string;
    seconds: string;
  } {
    let currentMonth = this.props.initialMonth;
    let startDate;
    let endDate;
    if (_isDate(value) || _isString(value)) {
      currentMonth = currentMonth ?? new Date(value);
      startDate = new Date(value);
    }
    if (
      value &&
      _isPlainObject(value) &&
      !_isString(value) &&
      !_isNumber(value) &&
      'min' in value
    ) {
      currentMonth =
        currentMonth ?? (value.min ? new Date(value.min) : new Date());
      startDate = value.min ? new Date(value.min) : undefined;
      endDate = value.max ? new Date(value.max) : undefined;
    }
    if (!currentMonth) {
      currentMonth = new Date();
    }
    const defaultTimeFormat =
      sessionStorage.getItem('timePickerTimeFormat') ?? getUserTimeFormat();
    const userTimeFormat = this.state?.selectedTimeFormat ?? defaultTimeFormat;
    const timePickerData = {
      timeFormat: userTimeFormat,
      hours: '12',
      minutes: '00',
      seconds: '00',
    };

    if (this.props.withTimePicker) {
      const { hours, minutes, seconds, timeFormat } = extractDateTimeInfo(
        startDate ?? new Date(),
      );
      timePickerData.timeFormat = userTimeFormat === '24h' ? '24h' : timeFormat;
      timePickerData.hours = validateHoursByTimeFormat(
        hours.toLocaleString(undefined, {
          minimumIntegerDigits: 2,
        }),
        timePickerData.timeFormat,
      );
      timePickerData.minutes = minutes.toLocaleString(undefined, {
        minimumIntegerDigits: 2,
      });
      timePickerData.seconds = seconds.toLocaleString(undefined, {
        minimumIntegerDigits: 2,
      });
    }

    return {
      currentMonth,
      startDate,
      endDate,
      selectedTimeFormat: timePickerData.timeFormat,
      hours: timePickerData.hours,
      minutes: timePickerData.minutes,
      seconds: timePickerData.seconds,
    };
  }

  renderHeader(): ReactNode {
    const dateFormat = 'MMMM yyyy';
    return (
      <div className={styles.header}>
        <div className={styles.header_date}>
          <button
            aria-label="Month/Year Button"
            className={classNames(
              styles.header_date_button,
              mixins.reset_button,
            )}
            onClick={this.onHeaderClick}
            type="button"
          >
            {format(this.state.currentMonth, dateFormat)}
          </button>
        </div>
        <div className={styles.navigate_month_buttons}>
          <IconButton
            className={styles.prev_month_button}
            icon={ICONS.CornerDown}
            onClick={this.prevMonth}
            data-prev="true"
          />
          <IconButton
            className={styles.next_month_button}
            icon={ICONS.CornerDown}
            onClick={this.nextMonth}
            data-next="true"
          />
        </div>
      </div>
    );
  }

  renderDays(): ReactNode {
    const dateFormat = 'EE';
    const days = [];
    const weekStart = startOfWeek(this.state.currentMonth);
    for (let i = 0; i < 7; i += 1) {
      days.push(
        <div className={styles.weekday} key={i}>
          {format(addDays(weekStart, i), dateFormat)}
        </div>,
      );
    }
    return <div className={styles.days}>{days}</div>;
  }

  renderCells(): ReactNode {
    const { currentMonth, startDate, endDate } = this.state;
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const weekStart = startOfWeek(monthStart);
    const weekEnd = endOfWeek(monthEnd);
    const dateFormat = 'd';
    const rows = [];
    const today = new Date();
    let days = [];
    let day = weekStart;
    while (day <= weekEnd) {
      for (let i = 0; i < 7; i += 1) {
        const formattedDate = format(day, dateFormat);
        const cloneDay = day;
        const isStartDate = startDate && isSameDay(day, startDate);
        const isEndDate = endDate && isSameDay(day, endDate);
        let isUnderMin;
        let isOverMax;
        const { minDate, maxDate } = this.props;
        if (minDate) {
          isUnderMin = isBefore(
            day,
            startOfDay(_isString(minDate) ? parseISO(minDate) : minDate),
          );
        }
        if (maxDate) {
          isOverMax = isAfter(
            day,
            startOfDay(_isString(maxDate) ? parseISO(maxDate) : maxDate),
          );
        }
        const disabled = isUnderMin || isOverMax;
        days.push(
          <div
            key={`${String(day)}:${currentMonth}`}
            className={classNames(styles.day, styles.cell, {
              [styles.day_disabled]: disabled,
              [styles.last_month]: !isSameMonth(day, monthStart),
              [styles.today]: isSameDay(day, today),
              [styles.range]:
                startDate &&
                endDate &&
                isAfter(day, startDate) &&
                isBefore(endOfDay(day), endDate),
              [styles.range_start]:
                startDate &&
                endDate &&
                isStartDate &&
                !isSameDay(startDate, endDate),
              [styles.range_end]:
                startDate &&
                endDate &&
                isEndDate &&
                !isSameDay(startDate, endDate),
            })}
          >
            <div
              className={classNames(styles.day_selection, {
                [styles.selected]: isStartDate || isEndDate,
              })}
            >
              <button
                aria-label="Day Button"
                className={classNames(mixins.reset_button, styles.day_button)}
                disabled={disabled}
                onClick={(event) => this.onDateClick(cloneDay, event)}
                data-cell={formattedDate}
                data-month={getMonth(day)}
                data-start={isStartDate || undefined}
                data-end={isEndDate || undefined}
              >
                {formattedDate}
              </button>
            </div>
          </div>,
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className={styles.week} key={String(day)}>
          {days}
        </div>,
      );
      days = [];
    }
    return <div>{rows}</div>;
  }

  renderDayPicker(): ReactNode {
    return (
      <>
        {this.renderHeader()}
        {this.renderDays()}
        {this.renderCells()}
      </>
    );
  }

  renderMonthYearPicker(): ReactNode {
    return (
      <div className={styles.months}>
        {this.renderMonthPicker()}
        {this.renderYearPicker()}
        <div className={styles.months_footer}>
          <Button onClick={this.onMonthSet}>Ok</Button>
        </div>
      </div>
    );
  }

  renderMonthPicker(): ReactNode {
    const dateFormat = 'MMM';
    const quarters = [];
    let months;
    let quarter = 0;
    let month = 0;
    while (quarter < 4) {
      months = [];
      for (let i = 0; i < 3; i += 1) {
        const newMonth = month;
        months.push(
          <Button
            noBorder
            wideBasis={false}
            onClick={(): void => {
              this.onMonthClick(setMonth(this.state.currentMonth, newMonth));
            }}
            key={i}
            variant={VARIANTS.WHITE}
            active={month === getMonth(this.state.currentMonth)}
          >
            {format(setMonth(new Date(), month), dateFormat)}
          </Button>,
        );
        month += 1;
      }
      quarters.push(
        <div className={styles.quarter} key={quarter}>
          {months}
        </div>,
      );
      quarter += 1;
    }
    return quarters;
  }

  renderYearPicker(): ReactNode {
    return (
      <InputNumber
        className={styles.year_picker}
        type="number"
        value={Number(format(this.state.currentMonth, 'yyyy'))}
        onChange={this.onYearSet}
        readOnly={this.props.disabled || this.props.readOnly}
        plusIcon={ICONS.Plus}
        minusIcon={ICONS.Minus}
        buttonVariant={VARIANTS.WHITE}
      />
    );
  }

  onDateClick = (day: Date | undefined, event: FormFieldEvent): void => {
    const { startDate, endDate } = this.state;
    const shouldSetDate = !this.props.withTimePicker; // Should update metadata field and close popover if time picker is not present
    if (
      (startDate && endDate) ||
      !this.props.isRangeMode ||
      (startDate && endDate && isSameDay(startDate, endDate))
    ) {
      return this.setState(
        {
          startDate: day,
          endDate: undefined,
        },
        () => (shouldSetDate ? this.onChange(event) : undefined),
      );
    }
    let isCorrectDate;
    if (!endDate) {
      isCorrectDate = startDate && day && isAfter(day, startDate);
      return this.setState(
        {
          endDate: isCorrectDate ? day : undefined,
          startDate: isCorrectDate ? startDate : day,
        },
        () => (shouldSetDate ? this.onChange(event) : undefined),
      );
    }
    return this.setState(
      {
        startDate: day,
      },
      () => (shouldSetDate ? this.onChange(event) : undefined),
    );
  };

  onChange(event: FormFieldEvent): void {
    const { startDate, endDate } = this.state;
    if (this.props.isRangeMode) {
      const min = startDate;
      const maxStart = startDate ? endOfDay(startDate) : undefined;
      const maxEnd = endDate ? endOfDay(endDate) : undefined;
      return this.props.onChange({ min, max: maxEnd ?? maxStart }, event);
    }
    return this.props.onChange(startDate, event);
  }

  onMonthClick = (month: Date): void => {
    this.setState({
      currentMonth: month,
    });
  };

  onMonthSet = (): void => {
    this.setState({
      yearMonthPicker: false,
    });
  };

  onYearSet = (value: number | undefined | null): void => {
    if (!value) {
      return;
    }
    this.setState({
      currentMonth: setYear(this.state.currentMonth, value),
    });
  };

  onHeaderClick = (): void => {
    this.setState({
      yearMonthPicker: true,
    });
  };

  nextMonth = (): void => {
    this.setState({
      currentMonth: addMonths(this.state.currentMonth, 1),
    });
  };

  prevMonth = (): void => {
    this.setState({
      currentMonth: subMonths(this.state.currentMonth, 1),
    });
  };

  onMouseDown: MouseEventHandler<HTMLDivElement> = (e): void => {
    const target = targetHtmlOrSvgElement(e.target);
    if (!target) {
      return;
    }
    if (target.dataset.start || target.dataset.end) {
      this.setState({ dragging: target.dataset.start ? 0 : 1 });
    }
  };

  onMouseMove: MouseEventHandler<HTMLDivElement> = (e): void => {
    if (this.state.dragging !== undefined) {
      const target = targetHtmlOrSvgElement(e.target);
      if (target && !Number.isNaN(Number(target.dataset.cell))) {
        const dragKey = stateMap[this.state.dragging];
        const draggingDate = this.state[dragKey];
        if (!draggingDate) {
          return;
        }
        const newDate = setMonth(
          setDate(draggingDate, Number(target.dataset.cell)),
          Number(target.dataset.month),
        );
        if (!isSameDay(newDate, draggingDate)) {
          switch (dragKey) {
            case 'startDate':
              this.setState({ startDate: newDate });
              break;
            case 'endDate':
              this.setState({ endDate: newDate });
              break;
            default:
              break;
          }
        }
      }
    }
  };

  onMouseUp = (event: FormFieldEvent): void => {
    const { dragging, startDate, endDate } = this.state;
    if (dragging !== undefined) {
      if (startDate && endDate && isAfter(startDate, endDate)) {
        this.onDateClick(this.state[stateMap[dragging]], event);
      } else {
        this.onChange(event);
      }
      this.setState({ dragging: undefined });
    }
  };

  render(): ReactNode {
    const { yearMonthPicker } = this.state;
    return (
      <div
        {...ui('calendar')}
        aria-label="Calendar"
        className={classNames(styles.calendar, {
          [styles.calendar_with_time_picker]: this.props.withTimePicker,
        })}
        onMouseDown={this.onMouseDown}
        onMouseMove={this.onMouseMove}
      >
        {this.renderDayPicker()}
        {yearMonthPicker && this.renderMonthYearPicker()}
        {!yearMonthPicker && this.props.withTimePicker && (
          <TimePicker
            hours={this.state.hours}
            minutes={this.state.minutes}
            seconds={this.state.seconds}
            selectedTimeFormat={this.state.selectedTimeFormat}
            onDone={this.props.onChange}
            startDate={this.state.startDate}
            setTime={(value, name) => {
              this.setState({ ...this.state, [name]: value });
            }}
            setTimeFormat={(value) => {
              const newHrsString = validateHoursByTimeFormat(
                this.state.hours ?? '12',
                value,
              );
              if (value !== this.state.selectedTimeFormat) {
                this.setState({
                  selectedTimeFormat: value,
                  hours: newHrsString,
                });
              }
            }}
          />
        )}
      </div>
    );
  }
}

export default Calendar;
